const IMPORT = {
  OPEN_MODAL: 'Import > Open modal',
}
const IMPORT_DOCUMENT = 'Import document'

// Import V2
const IMPORT_OPTIONS = {
  IMPORT_ONLY: 'Import slides',
  // AHA-47757: PPTX → editable content-v2 slides via the document-system
  // converter (see @/api/pptxConvert). The raw key doubles as the i18n key and
  // the Mixpanel `import_option` value. The "Beta" marker is a badge next to
  // the label (IMPORT_ACTION_OPTIONS[].beta), not part of the translated text.
  IMPORT_EDITABLE: 'Import as editable slides',
  IMPORT_AND_GENERATE: 'Import and generate slides with AI',
  GENERATE_ONLY: 'Generate interactive slides',
}

const IMPORT_ACTION_OPTIONS = [
  {
    value: IMPORT_OPTIONS.IMPORT_ONLY,
    // No need to send data to AI Chatbot for this option, so isImport is not set here
    tooltip: 'Import your file exactly as it is. You can use AI to generate interactive slides later.',
  },
  {
    value: IMPORT_OPTIONS.IMPORT_EDITABLE,
    // Not sent to the AI chatbot either — the conversion runs on the
    // document-system backend directly, so isImport stays unset (like
    // IMPORT_ONLY). pptx-only + flag gating: see ImportModalV2.availableActionOptions.
    pptxOnly: true,
    beta: true,
    tooltip: 'Convert your PowerPoint into editable slides. Text and shapes stay editable. Complex elements may be imported as images.',
  },
  {
    value: IMPORT_OPTIONS.IMPORT_AND_GENERATE,
    isImport: true,
    tooltip: 'Import your file as slides, then AI will suggest and insert interactive ones after relevant sections.',
  },
  {
    value: IMPORT_OPTIONS.GENERATE_ONLY,
    isImport: false,
    tooltip: 'Use AI to create interactive slides from your file content, without importing the originals.',
  },
]

const CLOSE_IMPORT_MODAL = 'close-import-modal'
const CLOSE_UPLOAD_MODAL = 'close-upload-modal'
const CLICK_BROWSE_FILE = 'click-browse-file'
// Cloud-sync (Google Drive / OneDrive) early-access fake-door funnel
const CLOUD_SYNC_CARD_CLICK = 'cloud-sync-card-click'
const CLOUD_SYNC_EARLY_ACCESS_CONFIRMED = 'cloud-sync-early-access-confirmed'
const CLOUD_SYNC_DECLINED = 'cloud-sync-declined'
const CLOUD_SYNC_SUBMITTED = 'cloud-sync-submitted'
// Client-side cloud file picker (Google/OneDrive SDK + auth), separate from the backend fetch
// authenticated/cancelled/failed carry a `stage` (auth|picker) so the OAuth-step dropoff is visible
const CLOUD_PICKER_AUTHENTICATED = 'cloud-picker-authenticated'
const CLOUD_PICKER_SUCCESSFUL = 'cloud-picker-successful'
const CLOUD_PICKER_CANCELLED = 'cloud-picker-cancelled'
const CLOUD_PICKER_POPUP_BLOCKED = 'cloud-picker-popup-blocked'
const CLOUD_PICKER_FAILED = 'cloud-picker-failed'
const CLOUD_PICKER_STAGE = { AUTH: 'auth', PICKER: 'picker' }
// Re-sync of an already-imported file, failures carry a `stage` (auth|metadata|import)
const CLOUD_SYNC_UPDATE_SUCCESSFUL = 'cloud-sync-update-successful'
const CLOUD_SYNC_UPDATE_FAILED = 'cloud-sync-update-failed'
const CLOUD_SYNC_UPDATE_STAGE = { AUTH: 'auth', METADATA: 'metadata', IMPORT: 'import' }
// Disconnect-from-source (unlink) — slides keep their content but stop syncing
const CLOUD_SYNC_UNLINK_SUCCESSFUL = 'cloud-sync-unlink-successful'
const CLOUD_SYNC_UNLINK_FAILED = 'cloud-sync-unlink-failed'
const UPLOAD_FILE_STARTED = 'upload-file-started'
const UPLOAD_FILE_SUCCESSFUL = 'upload-file-successful'
const UPLOAD_FILE_FAILED = 'upload-file-failed'
const START_IMPORTING = 'start-importing'
const IMPORT_V2_SUCCESSFUL = 'importing-successful'
const IMPORT_V2_FAILED = 'importing-failed'
// Why an import failed, so `importing-failed` can be broken down by cause
// rather than being one undifferentiated number.
//
// IMPORT_JOB is the only one that existed before: the async job itself
// reported failure, or the poll timed out. The other two cover
// "Import and generate slides with AI", which hands off to the chatbot-app
// over a one-way postMessage and until now reported nothing at all when that
// handoff failed — the user saw a spinner, Mixpanel saw a `start-importing`
// with no outcome, and only Sentry knew.
const IMPORT_FAILURE_REASON = {
  IMPORT_JOB: 'import-job',
  IFRAME_NOT_READY: 'iframe-not-ready',
  POST_MESSAGE: 'post-message',
  // Re-sync only: the provider fetch or the file download failed, so no import
  // job was ever created.
  CLOUD_DOWNLOAD: 'cloud-download',
  // AHA-47757 editable import only: the converter refused a second job for a
  // deck that already has one running (HTTP 409). Expected user behaviour
  // (cancel then retry, a second tab), not a failure of the import itself —
  // kept out of the IMPORT_JOB failure rate.
  ALREADY_RUNNING: 'already-running',
}
// Which surface started an import. The modal (Import only / Import as
// editable / Import and generate / Generate only) was the only emitter of the importing-* events
// until the cloud re-sync started reporting them too, so every event before
// that is MODAL — filtering on it recovers the original series exactly.
const IMPORT_TRIGGER = {
  MODAL: 'modal',
  CLOUD_RESYNC: 'cloud-resync',
}
const FEATURE_IMPORT_V2 = 'import-v2'

export {
  IMPORT,
  IMPORT_DOCUMENT,

  // Import V2
  IMPORT_OPTIONS,
  IMPORT_ACTION_OPTIONS,
  CLOSE_IMPORT_MODAL,
  CLOSE_UPLOAD_MODAL,
  CLICK_BROWSE_FILE,
  CLOUD_SYNC_CARD_CLICK,
  CLOUD_SYNC_EARLY_ACCESS_CONFIRMED,
  CLOUD_SYNC_DECLINED,
  CLOUD_SYNC_SUBMITTED,
  CLOUD_PICKER_AUTHENTICATED,
  CLOUD_PICKER_SUCCESSFUL,
  CLOUD_PICKER_CANCELLED,
  CLOUD_PICKER_POPUP_BLOCKED,
  CLOUD_PICKER_FAILED,
  CLOUD_PICKER_STAGE,
  CLOUD_SYNC_UPDATE_SUCCESSFUL,
  CLOUD_SYNC_UPDATE_FAILED,
  CLOUD_SYNC_UPDATE_STAGE,
  CLOUD_SYNC_UNLINK_SUCCESSFUL,
  CLOUD_SYNC_UNLINK_FAILED,
  UPLOAD_FILE_STARTED,
  UPLOAD_FILE_SUCCESSFUL,
  UPLOAD_FILE_FAILED,
  START_IMPORTING,
  IMPORT_V2_SUCCESSFUL,
  IMPORT_V2_FAILED,
  IMPORT_FAILURE_REASON,
  IMPORT_TRIGGER,
  FEATURE_IMPORT_V2,
}
